import { apiRequest } from '@/domains/api';
import type { IAdminVerifier } from './participant-contracts';

/**
 * Ranking cycles and the version each survey is pinned to inside them — SR-123's last
 * ticket, under `cycles.manage`.
 *
 * This is the surface that makes a published version *reachable*. Publishing a definition
 * does not put it in front of anybody: a respondent sees the version their cycle pins, so
 * until these routes existed a pin had to be written into the database by hand — which is
 * exactly what README's local-setup section used to tell people to do.
 *
 * ## The pin is the dangerous write
 *
 * `PUT /ranking-cycles/{code}/surveys/{survey}` repoints a live cycle at a different version.
 * Responses already open against the old one are **not** migrated and **not** closed: they
 * stay pinned where they were, because a response is frozen against the version it opened
 * with. That is deliberate, and the write says so out loud — it answers `retainedResponses`,
 * a count rather than an advisory sentence, so a caller can show the number and a test can
 * assert on it.
 *
 * ## Instants
 *
 * `opensAt` / `closesAt` and their cycle-level defaults are ISO strings on the wire, and the
 * server reads a naive one as UTC (`AssumedUtcInstantConverter`). A client sending a local
 * time without an offset is therefore not sending what it thinks, so the editor sends what
 * `<input type="datetime-local">` produces with an explicit `Z` appended or nothing at all.
 *
 * ## Who can start
 *
 * A pin's `config.access` (SR-487, survey-api's PFR §6): absent or `open` admits everyone
 * eligible; `listed` admits only the participants on the pin's access list and those already
 * holding a response in it. The list is read under `responses.browse` and written under
 * `rankings.manage`, not `cycles.manage`, and can be filled before the pin turns `listed`. The
 * pin's `PUT` replaces the whole config, so a writer of `access` sends the config's other keys
 * as it read them.
 */

const BASE = '/api/v1/admin/ranking-cycles';

/** `cycle.status`, from the vocabulary's `enums.cycleStatus`. */
export type CycleStatus = 'planned' | 'open' | 'closed' | 'archived';

/** One survey pinned inside a cycle. Carries **both** the version uuid and its number: a
 *  client needs the uuid to address `/versions/{id}` and the number to read the row. */
export interface ISurveyPin {
  surveyCode: string;
  surveyVersionId: string;
  versionNo: number;
  versionStatus: string;
  opensAt: Nullable<string>;
  closesAt: Nullable<string>;
  status: CycleStatus;
  /** Cycle-scoped render config — `as_of_date`, `year_labels` and the like — and `access`
   *  (`PinAccess`, see the module doc). */
  config: Nullable<unknown>;
  /** How many steps the pin's reminder plan holds; 0 with no plan. */
  reminderSteps: number;
}

/** Who a pin admits, `config.access`; absent reads as `open`. Any other value is survey-api's
 *  400 on `config`. */
export type PinAccess = 'open' | 'listed';

/** One participant on a pin's access list — `AdminCycleAccessEntryDto`: who added it and when. */
export interface ICycleAccessEntry {
  participantId: string;
  displayName: string;
  addedAt: string;
  addedBy: IAdminVerifier;
}

/** What an addition answers: the ids added, and those on the list already. */
export interface ICycleAccessAdded {
  added: string[];
  already: string[];
}

/** What a removal answers: the ids removed, and those not on the list. */
export interface ICycleAccessRemoved {
  removed: string[];
  absent: string[];
}

export interface IRankingCycle {
  code: string;
  name: string;
  status: CycleStatus;
  defaultOpensAt: Nullable<string>;
  defaultClosesAt: Nullable<string>;
  surveys: ISurveyPin[];
}

/** `status` is optional on a create — the server defaults it to `planned`, because a create
 *  has no previous value to lose — and **required** on both replaces. */
export interface ICreateCycle {
  code: string;
  name: string;
  status?: CycleStatus;
  defaultOpensAt?: Nullable<string>;
  defaultClosesAt?: Nullable<string>;
}

export interface ICycleWrite {
  name: string;
  status: CycleStatus;
  defaultOpensAt: Nullable<string>;
  defaultClosesAt: Nullable<string>;
}

/** The pin body. `survey` is a **code** and `versionNo` a **number**, never a uuid. */
export interface ISurveyPinWrite {
  survey: string;
  versionNo: number;
  status: CycleStatus;
  opensAt: Nullable<string>;
  closesAt: Nullable<string>;
  config: Nullable<unknown>;
}

/** What a pin write answers: the row, and what the write deliberately did **not** do. */
export interface ISurveyPinResult {
  survey: ISurveyPin;
  /** Responses left pinned to the version this cycle used to point at. */
  retainedResponses: number;
}

export const cyclesApi = {
  list(signal?: AbortSignal): Promise<IRankingCycle[]> {
    return apiRequest<IRankingCycle[]>(BASE, { signal });
  },

  get(cycleCode: string, signal?: AbortSignal): Promise<IRankingCycle> {
    return apiRequest<IRankingCycle>(`${BASE}/${cycleCode}`, { signal });
  },

  create(body: ICreateCycle): Promise<IRankingCycle> {
    return apiRequest<IRankingCycle>(BASE, { method: 'POST', body });
  },

  /** Whole-block: a member absent from the body is a cleared column, not an untouched one. */
  update(cycleCode: string, body: ICycleWrite): Promise<IRankingCycle> {
    return apiRequest<IRankingCycle>(`${BASE}/${cycleCode}`, { method: 'PUT', body });
  },

  pins(cycleCode: string, signal?: AbortSignal): Promise<ISurveyPin[]> {
    return apiRequest<ISurveyPin[]>(`${BASE}/${cycleCode}/surveys`, { signal });
  },

  /** Pins a survey into this cycle for the first time. */
  addPin(cycleCode: string, body: ISurveyPinWrite): Promise<ISurveyPinResult> {
    return apiRequest<ISurveyPinResult>(`${BASE}/${cycleCode}/surveys`, {
      method: 'POST',
      body
    });
  },

  /** Repoints an existing pin. Open responses stay where they are — see the module doc. */
  updatePin(
    cycleCode: string,
    surveyCode: string,
    body: ISurveyPinWrite
  ): Promise<ISurveyPinResult> {
    return apiRequest<ISurveyPinResult>(`${BASE}/${cycleCode}/surveys/${surveyCode}`, {
      method: 'PUT',
      body
    });
  },

  /** The pin's access list, every admitted participant by name — unpaged; 404 for an unknown
   *  pin. */
  access(cycleCode: string, surveyCode: string, signal?: AbortSignal): Promise<ICycleAccessEntry[]> {
    return apiRequest<ICycleAccessEntry[]>(accessPath(cycleCode, surveyCode), { signal });
  },

  /** Adds 1 to 1 000 participants. A participant already on the list is reported in `already`,
   *  not refused. 400 at `participantIds[i]` for an id that names no participant of the survey's
   *  subject kind, and nothing is written. */
  addAccess(
    cycleCode: string,
    surveyCode: string,
    participantIds: string[]
  ): Promise<ICycleAccessAdded> {
    return apiRequest<ICycleAccessAdded>(accessPath(cycleCode, surveyCode), {
      method: 'POST',
      body: { participantIds }
    });
  },

  /** Removes participants; one not on the list is reported in `absent`. A response already open
   *  in the cycle stays. 400 at `participantIds[i]` as for an addition. */
  removeAccess(
    cycleCode: string,
    surveyCode: string,
    participantIds: string[]
  ): Promise<ICycleAccessRemoved> {
    return apiRequest<ICycleAccessRemoved>(`${accessPath(cycleCode, surveyCode)}/removals`, {
      method: 'POST',
      body: { participantIds }
    });
  }
};

function accessPath(cycleCode: string, surveyCode: string): string {
  return `${BASE}/${encodeURIComponent(cycleCode)}/surveys/${encodeURIComponent(surveyCode)}/access`;
}
